import React, { useState, useEffect, useMemo } from "react";
import { Formik, Field, Form } from "formik";
import {
  InputGroup,
  FieldWrappers,
  Label,
  SubmitBtn,
  SideCancelBtn,
  Wrapper,
  ScrollableContent,
  ActionSection,
  MobileIcon,
  CombineInputGroup,
  CardWrapper,
  SelectionCard,
  ModalHeader,
} from "../Styles/InputStyles";
import tw from "tailwind-styled-components";
import { MdClose, MdEdit } from "react-icons/md";
import * as Yup from "yup";
import { enquirySourceOptions, propertyFeatures } from "../../Config";
import SelectOptionModal from "../SelectOptionModal";
import { FormControl, MenuItem, Select } from "@mui/material";
import { useSelector } from "react-redux";
import SelectLocationModal from "../SelectLocationModal";
import SelectPreferenceModal from "../SelectPreferenceModal";
import {
  furnishedOptions,
  preferredTenantOptions,
  rentRange,
} from "../../helpers";

const EditEnquiry = ({
  editEnquiriesLoading,
  editEnquiriesMutate,
  setIsEditEnquiries,
  selectedData,
}) => {
  const [height, setHeight] = useState(window.innerHeight);
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const locations = useSelector((state) => state.LocationsReducer.locations);

  const filteredLocations = useMemo(
    () => locations?.filter((l) => l.id !== 0) || [],
    [locations],
  );

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const initialValues = {
    fullName: selectedData?.name,
    mobile: selectedData?.mobile,
    locationId: selectedData?.locationId || "",
    rentRange: selectedData?.rentRange || "",
    source: selectedData?.source || "",
    preferences: selectedData?.preferences
      ? JSON.parse(selectedData.preferences)
      : [],
    furnishedType: selectedData?.furnishingType || "",
    preferredTenantType: selectedData?.tenantPreference || "",
    note: selectedData?.note || "",
  };

  const handleSubmit = (values, { resetForm }) => {
    editEnquiriesMutate({
      name: values.fullName,
      mobile: values.mobile,
      locationId: values.locationId,
      rentRange: values.rentRange || "",
      source: values.source || "",
      preferences: JSON.stringify(values.preferences || []),
      tenantPreference: parseInt(values.preferredTenantType || ""),
      furnishingType: parseInt(values.furnishedType || ""),
      id: selectedData?.id,
      note: values.note || "",
    });
  };

  const validationSchema = Yup.object({
    fullName: Yup.string()
      .required("Name is required")
      .matches(
        /^[A-Za-z0-9.\s]+$/,
        "Only letters, numbers, spaces and dot are allowed.",
      )
      .min(4, "Name must be at least 4 characters long"),
    mobile: Yup.string()
      .required("Mobile number is required")
      .matches(/^[0-9]{10}$/, "Mobile must be 10 digits"),
    locationId: Yup.string().required("Location is required"),
    rentRange: Yup.string().required("Rent range is required"),
    source: Yup.number().required("Source is required"),
    furnishedType: Yup.string().nullable(),
    preferredTenantType: Yup.string().nullable(),
    // preferences: Yup.array()
    //     .min(1, "At least one preference is required")
    //     .required("Preference is required"),
  });

  return (
    <Container
      style={{
        height: `${height}px`,
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <ModalHeader>
        <h1 className="text-white text-lg font-medium">Edit Enquiries</h1>
        <button onClick={() => setIsEditEnquiries(false)}>
          <MdClose color="white" size={24} />
        </button>
      </ModalHeader>

      <div className="px-6 h-full flex flex-col justify-between">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, resetForm, errors, touched }) => {
            const getSelectedLabels = () => {
              if (!values.preferences?.length) return "Select Preference";

              const selected = propertyFeatures.filter((f) =>
                values.preferences.includes(f.value),
              );

              return selected.map((f) => f.label).join(", ");
            };

            const getSelectedSource = () => {
              if (!values.source) return "Select Source";

              const selected = enquirySourceOptions.find(
                (s) => s.value === values.source,
              );

              return selected ? selected.label : "Select Source";
            };

            const handleFeaturesSelect = (selectedValues) => {
              setFieldValue("preferences", selectedValues);
            };

            const getLocationLabel = () => {
              const selectedLocation = filteredLocations.find(
                (loc) => loc.id === values.locationId,
              );
              return selectedLocation
                ? selectedLocation.name
                : "Select Location";
            };

            return (
              <Form>
                <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                  <Wrapper>
                    <InputGroup>
                      <Label htmlFor="fullName">Name *</Label>
                      <FieldWrappers>
                        <Field
                          type="text"
                          name="fullName"
                          id="fullName"
                          autoComplete="off"
                          placeholder="Enter name"
                        />
                      </FieldWrappers>
                      {errors.fullName && touched.fullName && (
                        <ErrorText>{errors.fullName}</ErrorText>
                      )}
                    </InputGroup>
                    <InputGroup>
                      <Label htmlFor="mobile">Mobile *</Label>
                      <FieldWrappers className="bg-gray-100">
                        <MobileIcon />
                        <Field
                          type="text"
                          name="mobile"
                          id="mobile"
                          autoComplete="off"
                          maxLength={10}
                          placeholder="Enter 10-digit mobile number"
                          className="w-full pl-5"
                          required
                          readOnly
                        />
                      </FieldWrappers>
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="rentRange">Rent Range *</Label>
                      <FormControl fullWidth>
                        <Select
                          className="px-2"
                          value={values.rentRange}
                          onChange={(e) =>
                            setFieldValue("rentRange", e.target.value)
                          }
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return (
                                <span className="text-gray-400">
                                  Select rentrange
                                </span>
                              );
                            }
                            const option = rentRange.find(
                              (opt) => opt.value === selected,
                            );
                            return option ? option.label : selected;
                          }}
                          sx={{ height: "46px" }}
                        >
                          <MenuItem value="" disabled>
                            <span className="text-gray-400">
                              Select Rentrange
                            </span>
                          </MenuItem>
                          {rentRange.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {touched.rentRange && errors.rentRange && (
                        <p className="text-rose-600 text-sm">
                          {errors.rentRange}
                        </p>
                      )}
                    </InputGroup>
                    <CombineInputGroup>
                      <InputGroup>
                        <Label>Select Location *</Label>
                        <FieldWrappers
                          onClick={() => setShowLocationModal(true)}
                        >
                          <Field
                            readOnly
                            value={getLocationLabel()}
                            className="w-full bg-transparent border-none outline-none cursor-pointer"
                          />
                          <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                        </FieldWrappers>
                        {touched.locationId && errors.locationId && (
                          <div className="min-h-[20px] mt-1">
                            <p className="text-rose-600 text-sm">
                              {errors.locationId}
                            </p>
                          </div>
                        )}
                      </InputGroup>

                      <InputGroup>
                        <Label>Source *</Label>
                        <FieldWrappers onClick={() => setShowSourceModal(true)}>
                          <Field
                            type="text"
                            readOnly
                            value={getSelectedSource()}
                            className="w-full border-none outline-none bg-transparent cursor-pointer"
                          />
                          <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                        </FieldWrappers>
                        {touched.source && errors.source && (
                          <div className="min-h-[20px] mt-1">
                            <p className="text-rose-600 text-sm">
                              {errors.source}
                            </p>
                          </div>
                        )}
                      </InputGroup>
                    </CombineInputGroup>
                    <InputGroup>
                      <Label>Preference</Label>

                      <FieldWrappers onClick={() => setShowFeaturesModal(true)}>
                        <Field
                          type="text"
                          readOnly
                          value={getSelectedLabels()}
                          className="w-full border-none outline-none bg-transparent cursor-pointer"
                        />

                        <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                      </FieldWrappers>

                      {touched.preferences && errors.preferences && (
                        <ErrorText>{errors.preferences}</ErrorText>
                      )}
                    </InputGroup>

                    <InputGroup>
                      <Label>Furnished Type</Label>
                      <CardWrapper>
                        {furnishedOptions.map((option) => (
                          <SelectionCard
                            key={option.value}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setFieldValue("furnishedType", option.value);
                            }}
                            className={
                              values.furnishedType === option.value
                                ? "bg-[#26A69A] text-white"
                                : "bg-gray-50 text-gray-800"
                            }
                          >
                            <span className="text-sm">{option.label}</span>
                          </SelectionCard>
                        ))}
                      </CardWrapper>
                      {touched.furnishedType && errors.furnishedType && (
                        <p className="text-rose-600 text-sm mt-1">
                          {errors.furnishedType}
                        </p>
                      )}
                    </InputGroup>

                    <InputGroup>
                      <Label>Preferred Tenant Type</Label>
                      <CardWrapper>
                        {preferredTenantOptions.map((option) => (
                          <SelectionCard
                            key={option.value}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setFieldValue(
                                "preferredTenantType",
                                option.value,
                              );
                            }}
                            className={
                              values.preferredTenantType === option.value
                                ? "bg-[#26A69A] text-white"
                                : "bg-gray-50 text-gray-800"
                            }
                          >
                            <span className="text-sm">{option.label}</span>
                          </SelectionCard>
                        ))}
                      </CardWrapper>
                      {touched.preferredTenantType &&
                        errors.preferredTenantType && (
                          <p className="text-rose-600 text-sm mt-1">
                            {errors.preferredTenantType}
                          </p>
                        )}
                    </InputGroup>
                    <InputGroup>
                      <Label htmlFor="note">Notes</Label>
                      <FieldWrapper>
                        <Field
                          as={Textarea}
                          id="note"
                          name="note"
                          rows={3}
                          maxLength={500}
                          placeholder="Write your note here..."
                        />
                      </FieldWrapper>
                    </InputGroup>
                  </Wrapper>
                </ScrollableContent>
                <ActionSection>
                  <SideCancelBtn
                    type="button"
                    onClick={() => setIsEditEnquiries(false)}
                  >
                    Cancel
                  </SideCancelBtn>
                  <SubmitBtn type="submit" disabled={editEnquiriesLoading}>
                    {editEnquiriesLoading ? "Updating..." : "Update"}
                  </SubmitBtn>
                </ActionSection>

                <SelectPreferenceModal
                  isOpen={showFeaturesModal}
                  onClose={() => setShowFeaturesModal(false)}
                  options={propertyFeatures}
                  selectedValue={values.preferences}
                  onSelect={handleFeaturesSelect}
                  title="Select Preference"
                  searchPlaceholder="Search Preference..."
                />

                <SelectOptionModal
                  isOpen={showSourceModal}
                  onClose={() => setShowSourceModal(false)}
                  title="Select Source"
                  options={enquirySourceOptions}
                  selectedValue={values.source}
                  onSelect={(val) => {
                    setFieldValue("source", Number(val));
                    setShowSourceModal(false);
                  }}
                  searchPlaceholder="Search Source..."
                />

                <SelectLocationModal
                  isOpen={showLocationModal}
                  onClose={() => setShowLocationModal(false)}
                  options={filteredLocations}
                  selectedValue={values.locationId}
                  onSelect={(val) => {
                    setFieldValue("locationId", val);
                    setShowLocationModal(false);
                  }}
                />
              </Form>
            );
          }}
        </Formik>
      </div>
    </Container>
  );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;
const FieldWrapper = tw.div`w-full`;
const Textarea = tw.textarea`min-h-30 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600  focus:border-sky-600 focus:outline-none duration-200 w-full h-30 resize-y  break-words overflow-hidden`;

export default EditEnquiry;
